/*
 * ---------------------------------------------------------------------------
 * COMMERCIAL IN CONFIDENCE
 *
 * (c) Copyright Quosient Ltd. All Rights Reserved.
 *
 * See LICENSE.txt in the repository root.
 * ---------------------------------------------------------------------------
 */

/*
* Add new layer to the map
* User can select which bands to visualize as RGB
*/
import * as Blockly from 'blockly/core';
import 'blockly/python'
import { CustomFieldDate } from '../../blocklyFieldDate';
import { AreaService } from '@/services/area.service';
import booleanWithin from "@turf/boolean-within";
import { getCoordinatesForPolygon } from '@/blocks/helper_functions';
import { polygon } from "@turf/helpers"
import countryBboxes from "@/countryData/bboxes.json";
import bboxPolygon from "@turf/bbox-polygon";
import intersect from "@turf/intersect";
import { DatasetService } from '@/services/dataset.service';
import { ManageDialogsService } from '@/services/manageDialogs.service';
import map_image from "@/assets/map_dark_24dp.svg"
import open_in_browser_image from "@/assets/open_in_browser_dark_24dp.svg"
import info_image from "@/assets/info_dark_24dp.svg"
import { areaChange } from '../helper_functions';

const GEE_TYPE_IMAGE = "image";
const GEE_TYPE_IMAGE_COLLECTION = "image_collection";
const WARNING_ID = {
    AREA_WARNING: "area_warning",
    TO_DATE_BEFORE_FROM_DATE: "TO_DATE_BEFORE_FROM_DATE",
    NO_DATES: "NO_DATES",
    DATE_BEFORE_START: "DATE_BEFORE_START",
    DATE_AFTER_END: "DATE_AFTER_END",
    EMPTY_DATE_RANGE: "EMPTY_DATE_RANGE",
    NODATA: "NODATA",
    BADTYPE: "BADTYPE",
    NOBANDS: "NOBANDS",
    NOFILENAME: "NOFILENAME",
    META_MISSING: "META_MISSING",
    INVALID_SCALE: "INVALID_SCALE",
    NO_RESOLUTION: "NO_RESOLUTION"
}

Blockly.Blocks['input_data_selector'] = {
    init: function () {
        let areas = AreaService.getAreas();
        // let datasetTitles = DatasetService.getDatasetTitles();
        this.appendDummyInput("title")
            .appendField(new Blockly.FieldLabel('EE Data Downloader'))

        this.appendDummyInput("datasets_link")
            .appendField("Browse datasets ")
            .appendField(new Blockly.FieldImage(open_in_browser_image, 24, 24, "Click here for information on extracting the EE Asset ID", this.browseDatasets), "helpIcon");
        this.getField("helpIcon").setTooltip("Click here for information on extracting the EE Asset ID")

        this.appendDummyInput("dataset_id")
            .appendField("EE Asset ID: ")
            .appendField(new Blockly.FieldTextInput("Select EE ID"), "catalogID")
            .appendField(new Blockly.FieldImage(map_image, 24, 24, "Click here to open the EarthEngine Dataset Catalog entry", this.openUrl), "datasetUrlIcon");

        this.getField("datasetUrlIcon").setVisible(false);
        this.getField("datasetUrlIcon").setTooltip("Click here to open the EarthEngine Dataset Catalog entry")

        this.appendDummyInput('caveatInput')
            .appendField('EE data could be restricted for commercial use', 'licenseCaveatText')
            .appendField(new Blockly.FieldImage(info_image, 24, 24, "Find out more", this.licenseNotCommercial), 'licenseCaveat')
            .setVisible(false);

        this.appendDummyInput() // just a blank row 
        
        this.appendDummyInput("temporal_extent")
            .appendField("From:     ")
            .appendField(new CustomFieldDate(null, null, 'true'), 'fd')
            .appendField('      To:    ')
            .appendField(new CustomFieldDate(null, null, 'true'), 'td')
            .setVisible(false)
        
        
        this.appendDummyInput("spatial_extent")
            .appendField("Area: ")
            .appendField(new Blockly.FieldDropdown(areas), "area")
            .setVisible(false)
            
            
        this.appendDummyInput() // just a blank row 
           
        this.appendDummyInput("composite_method")
            .appendField("Compose into image with method: ")
            .appendField(new Blockly.FieldDropdown([["Mosaic", "mosaic"],["Mean", "mean"], 
            ["Median", "median"], ["Min", "min"], ['Max', 'max']]), "comp_method")
            .setVisible(false)
           
            
       
        this.appendDummyInput("filename")
        .appendField("File Name: ")
        .appendField(new Blockly.FieldTextInput("filename"), "downloadfilename")
        .appendField(" .tif")

       
        this.appendDummyInput('GeotiffLabel')
            .appendField('Geotiff format options') ;
        this.appendDummyInput("COG")
            .appendField("    COG Format")
            .appendField(new Blockly.FieldCheckbox(false), 'cloudOptimised')
        this.appendDummyInput("geotiff_options")
            .appendField("    Skip blanks")
            .appendField(new Blockly.FieldCheckbox(true), 'skipBlanks')
            
        this.appendDummyInput("scale")
            .appendField("    Scale: ")
            .appendField(new Blockly.FieldDropdown([["Native", "default"], ["Manual", "manual"], ["x 1.25", "scale125percent"], ["x 1.5", "scale150percent"], ["x 2.0", "scale200percent"]]), "scalefactor")
            .appendField(new Blockly.FieldNumber(0,0,5000,1), "scaleNumber")
        
        this.setStyle('dataDownloader');
      
        this.eventsubscription = AreaService.shapeUpdated$.subscribe(function() {
            this.setWarningText(null, WARNING_ID.AREA_WARNING);
            this.validate() ;     
        }.bind(this));
     
        this.eventsubscription = AreaService.clearAreas$.subscribe(function() {
            this.setWarningText(null, WARNING_ID.AREA_WARNING);
            this.validate() ;     
        }.bind(this));   
        
        this.areaSubscription = AreaService.areaChange$.subscribe( () => {
            let result = areaChange(this, "spatial_extent", "area");
            if(!result) {
                this.areaSubscription.unsubscribe();
            }
        });
    },
    /**
    * lastSelectedCompMethod keeps track of the users selection of composite method
    * so that we can detect if it changed since last block update
    * @type {!string}
    */
    lastSelectedCompMethod:"mosaic",
    /**
    * lastSelectedDataset keeps track of the users current dataset selection
    * so that we can detect if it changed since last block update
    * @type {!string}
    */
    lastSelectedDataset:"Select EE ID",
    /**
    * lastSelectedScaleFactor keeps track of the scaleFactor selection
    * as a change has side effects which tirger subsequent block update we 
    * need to detect that this has changed to avoid an infinate feedback loop
    * @type {!string}
    */
    lastSelectedScaleFactor:"default",
    /**
     * variable keeps track of number of errors in setWarningText so that items can
     * be numbered
     * @type {number}
     */
    countErrors:0,
    
    /**
     * variable to track selected dataset EE catalog URL
     */
    datasetUrl: null,
    /**
    * checks that user date selection is in bounds specified in dataset metadata
    * @param {!string} dataID Earthengine dataset ID to check date range extent against.
    * @param {number} countErrors keeps track of how many setTextWarning have been set for the block  
    */
    validateDates: function(dataID){
        let fd = this.getFieldValue('fd');
        let td = this.getFieldValue('td');
        let fd_date = new Date(fd);
        let to_date = new Date(td);

        console.log("validating dates", fd_date, "to", to_date)
        if (to_date < fd_date) {
            this.setWarningText(this.countErrors++ +". To date cannot be earlier than from date\n", WARNING_ID.TO_DATE_BEFORE_FROM_DATE) ;
        }
        else
        {
            this.setWarningText(null, WARNING_ID.TO_DATE_BEFORE_FROM_DATE);
        }
        
        let metadata = this.getDatasetMetaData(dataID);
        if(!metadata.extent || !metadata.extent.temporal.interval)
        {
            this.setWarningText(this.countErrors++ +". Dataset metadata invalid: Unexpected missing temporal extent", WARNING_ID.NO_DATES) ;
        }
        else
        {
            this.setWarningText(null, WARNING_ID.NO_DATES);
       
            let temporalExtent = metadata.extent.temporal.interval ;
            let extentFrom = temporalExtent[0] ;
            let extentFromDate  = new Date(extentFrom);

            let prettyFromDate = extentFromDate.getFullYear() + "-" + (extentFromDate.getMonth() + 1) + "-" + extentFromDate.getDate();
            let extentTo = temporalExtent[1];
            let extentToDate = null;
            let prettyToDate = null;
            if(extentTo) {
                extentToDate = new Date(extentTo);
                prettyToDate = extentToDate.getFullYear() + "-" + (extentToDate.getMonth() + 1) + "-" + extentToDate.getDate();
            } else {
                prettyToDate = this.getDateOffset(new Date().toISOString(), 0,0,5);
            }

            
            let compareExtentFrom = this.makeDateTimeless(extentFromDate);
            if ( fd_date < compareExtentFrom) {
                this.setWarningText(this.countErrors++ +". The from date is outside the dataset range\n(" + prettyFromDate +" to " + prettyToDate + ").", WARNING_ID.DATE_BEFORE_START ) ;  
            } else {
                this.setWarningText(null, WARNING_ID.DATE_BEFORE_START );
            }

            if(extentToDate !== null)
            {
                let compareExtentTo = this.makeDateTimeless(extentToDate);
                if ( to_date > compareExtentTo) {
                    this.setWarningText(this.countErrors++ + ". The to date is outside the dataset range\n(" + prettyFromDate +" to " + prettyToDate + ").", WARNING_ID.DATE_AFTER_END ) ;
                } else {
                    this.setWarningText(null, WARNING_ID.DATE_AFTER_END );
                }
            }
            console.log("is fd_date equal to td_date", fd_date.getTime() === to_date.getTime())
            if (fd_date.getTime() === to_date.getTime()) {
                if (extentToDate !== null){
                    if (extentToDate.getTime() !== extentFromDate.getTime()) {
                        this.setWarningText(this.countErrors++ + ". To date cannot be equal to the start date \n for this dataset (Empty Date Range)", WARNING_ID.EMPTY_DATE_RANGE);
                    } else {
                        this.setWarningText(null, WARNING_ID.EMPTY_DATE_RANGE)
                    }
                } else {
                    this.setWarningText(this.countErrors++ + ". To date cannot be equal to the start date \n for this dataset (Empty Date Range)", WARNING_ID.EMPTY_DATE_RANGE);
                }
            } else {
                this.setWarningText(null, WARNING_ID.EMPTY_DATE_RANGE)
            }
        }    
    },

    makeDateTimeless: function(d) {
        let newDate = new Date(d);
        newDate.setUTCHours(0); 
        newDate.setUTCMinutes(0); 
        newDate.setUTCSeconds(0);
        newDate.setUTCMilliseconds(0)
        return newDate
    },

    /**
     * Checks if a block is assigned an area other that NA (does not currently 
     * check if those areas have polygons) then sets warning text
     */

    /**
    * checks that an AOI is assiged and that area is within bounds of dataset  
    * @param {!string} dataID Earthengine dataset ID to check spatial extent against.
    * @param {number} countErrors keeps track of how many setTextWarning have been set for the block  
    */

    validateArea: function (dataID) {
        let area = this.getFieldValue("area");
        console.log("validateArea: " + area + " with  datasetID: " + dataID );
        let metadata = this.getDatasetMetaData(dataID);

        if(area !== null) {
            this.setWarningText(null, WARNING_ID.AREA_WARNING);
            if(area === "NA"){
                console.log("NO AREA SELECTED")
                this.setWarningText(this.countErrors++ + ". Please select an area", WARNING_ID.AREA_WARNING);
            }
            // validating user areas
            else if (area.startsWith("Area")) {
                let polygons = AreaService.getShapesForArea(area);
                if(!polygons.length > 0) {
                    // ERROR: this will fail if user creates an area adds a polygon then removes it (okay maybe not, seems to be rly hit or miss)
                    this.setWarningText(this.countErrors++ + ". Area does not contain any polygons", WARNING_ID.AREA_WARNING);
                } 
                else if(polygons.length > 1) {
                    this.setWarningText(this.countErrors++ + ". Selected area has multiple polygons \n which is not supported for image exports.\nUse the CLEAR MAP button to delete all polygons,\n or to delete individual polygons press the Bin button \nand click on the polygon you want to remove.\n", WARNING_ID.AREA_WARNING);
                }
                else if(metadata.extent && metadata.extent.spatial) {
                    // attempts to validate that user polygon is wholly inside the bounding box of the dataset
                    // if not it checks to see if it intersects the bbox, i.e. it will return some data
                    // this will be used later to inform user that it might not get all the data they want
                    let bbox = metadata.extent.spatial.bbox;
                    let areaPolygons = AreaService.getShapesForArea(area);
                    // only allow a single area
                    let areaPolygon = areaPolygons[0].overlay;
                    let isPolygon = false;

                    if (areaPolygon.getPath) {
                        isPolygon = true;
                    }
                    
                    let formattedCoordinates = getCoordinatesForPolygon(areaPolygon)
                    let coords = []

                    formattedCoordinates.forEach(coordPair => {
                        coords.push([coordPair.lng, coordPair.lat])
                    })

                    // adds beginning coords to finish linear ring
                    if(isPolygon){
                        coords.push([formattedCoordinates[0].lng, formattedCoordinates[0].lat])
                    }

                    let coordsPolygon = polygon([coords])
                    let eeBboxPolygon = bboxPolygon(bbox)

                    if(!booleanWithin(coordsPolygon, eeBboxPolygon)){ // checks within bbox
                        if(intersect(coordsPolygon, eeBboxPolygon) === null){ // checks it intersects with bbox
                            // not a valid area
                            this.setWarningText(this.countErrors++ + ". Selected area contains no data", WARNING_ID.AREA_WARNING)
                        }
                    }
                }
            }
            else {
                if(metadata.extent && metadata.extent.spatial) {
                    // attempts to validate the bbox of a country is within the bbox of the dataset
                    // or at least intersects the bbox
                    // known issues - bboxes.json does not much our country list dataset, it will often not find the country
                    // for example I have converted the UK's 2 character code but there are plenty more
                    // if country is not known, validation is skipped - not an ideal solution!!
                    // long term solution would require some form of API though
                    let bbox = metadata.extent.spatial.bbox;
                    if(area === "UK") {area = "GB"}
                    
                    // filters for country selected
                    let countryData = countryBboxes.filter(countryData => countryData.country === area);
                    
                    if (countryData.length !== 0){
                        countryData = countryData[0]
                        let countryBbox = [
                            parseFloat(countryData.ne_lng), 
                            parseFloat(countryData.ne_lat), 
                            parseFloat(countryData.sw_lng), 
                            parseFloat(countryData.sw_lat)
                        ]

                        let countryBboxPolygon = bboxPolygon(countryBbox);
                        let eeBboxPolygon = bboxPolygon(bbox);
                        
                        if(!booleanWithin(countryBboxPolygon, eeBboxPolygon)){
                            if(intersect(countryBboxPolygon, eeBboxPolygon) === null){
                                // not a valid area
                                this.setWarningText(this.countErrors++ + ". Selected region contains no data", WARNING_ID.AREA_WARNING);
                            }
                        }
                    } else {
                        console.error(`could not find country to validate area - ${area}`)
                        // skips validation
                    }
                }
            }
        }
    },
    /**
    * runs through existing warnings on block and removes them 
    */
    clearWarnings: function() {
        if (this.warning) {
            let warnings = this.warning.text_;
            if (warnings !== null ) {
                for (const key of Object.entries(warnings)) {
                    this.setWarningText(null, key[0]);
                    
                }
            }
        }
    },
    /**
    * validates block using STAC metadata 
    * sets one or more warning texts for each validation issue
    */
    validate: function() {
        this.clearWarnings();
        this.countErrors = 1 ;
        
        let dataID = this.getFieldValue("catalogID");
        let metadata = this.getDatasetMetaData(dataID);
        let scaleFactor = this.getFieldValue("scalefactor");

        let caveatInput = this.getInput('caveatInput');
        let caveatLabel = this.getField('licenseCaveatText');
        let caveatField = this.getField('licenseCaveat');

        if (caveatField && caveatLabel) {
            console.log('REMOVING CAVEAT RE LICENSES')

            caveatInput.removeField('licenseCaveatText');
            caveatInput.removeField('licenseCaveat');
        }
        

        if(dataID==="Select EE ID" || dataID==="" || metadata == null)
        {   
            this.setWarningText(this.countErrors++ + ". Select a valid Earth Engine Image or ImageCollection ID\n", WARNING_ID.NODATA);
        }
        else
        {
            this.setWarningText(null, WARNING_ID.NODATA);
        }

        let downloadFilename = this.getFieldValue("downloadfilename");
        if(!downloadFilename || downloadFilename === "filename" )
        {
            this.setWarningText(this.countErrors++ + ". Please provide a filename for the download\n", WARNING_ID.NOFILENAME);
        }
        else
        {
            this.setWarningText(null, WARNING_ID.NOFILENAME);
        }

        //TODO VALIDATE characters in filename

        if (metadata) {
            if (!metadata.gsd && scaleFactor !== 'manual') {   
                this.setWarningText(this.countErrors++ + ". Earth Blox was unable to obtain the native resolution for this dataset from available metadata.\nPlease enter the resolution manually.", WARNING_ID.NO_RESOLUTION);
                return; 
            } else {
                this.setWarningText(null, WARNING_ID.NO_RESOLUTION);
            }
    
    
            if (metadata && !metadata.licence_is_commercial) {
                
                console.log('RE-ADDING CAVEAT RE LICENSES')

                caveatInput.appendField('EE data could be restricted for commercial use', 'licenseCaveatText');
                caveatInput.appendField(new Blockly.FieldImage(info_image, 24, 24, "Find out more", this.licenseNotCommercial), 'licenseCaveat');
                
                caveatInput.setVisible(true)
            }
    
            // check dataset is either an Image of Image Collection type
            if( ! (metadata.gee_type == GEE_TYPE_IMAGE_COLLECTION 
                || metadata.gee_type == GEE_TYPE_IMAGE ))
            {
                this.setWarningText( this.countErrors++ +  ". Data type <" + metadata.gee_type + 
                    "> not supported. \n Selected dataset must be either Image or ImageCollection type\n", WARNING_ID.BADTYPE);            
            }
            else
            {
                this.setWarningText(null, WARNING_ID.NODATA);
            }
    
    
            if( ! metadata.bands || metadata.bands.length < 1)
            {
                this.setWarningText(this.countErrors++ + ". Could not extract band information from metadata for this dataset\n", WARNING_ID.NOBANDS);
                
            }
            else
            {
                this.setWarningText(null, WARNING_ID.NOBANDS);
            }
            
            if(!metadata.extent || !metadata.extent.temporal ||
            !metadata.extent.temporal.interval)
            {
                this.setWarningText(this.countErrors++ + ". Selected dataset missing temporal extent data\n", WARNING_ID.META_MISSING);
            
            }
            else
            {
                this.setWarningText(null,WARNING_ID.META_MISSING);
                this.validateDates(dataID, this.countErrors) ;
            }
            if(metadata.gee_type == GEE_TYPE_IMAGE_COLLECTION)
            {
                this.validateArea(dataID, this.countErrors);
            }
    
        }
        // check scale is a number - not sure this is still needed as set 
        let scale = this.getFieldValue("scaleNumber" );
        if ( ! Number.isInteger(scale) )
        {
            this.setWarningText(this.countErrors++ + ". Scale must be a whole number (integer)\n", WARNING_ID.INVALID_SCALE);
        }
        else
        {
            this.setWarningText(null,WARNING_ID.INVALID_SCALE);
        }
    },

    /**
    * dynamically updates the block depending on users selection
    * 
    */
    update: function()
    {
        console.log('UPDATE')
        let datasetChanged = false ;
        let scaleFactorChanged = false ;
        let compMethodChanged = false ;
        let dataID = this.getFieldValue("catalogID");
        let scaleFactor = this.getFieldValue("scalefactor");
        let compMethod = this.getFieldValue("comp_method");

        // did the choice of dataset change
        if(dataID !== this.lastSelectedDataset)
        {
            datasetChanged = true;
            this.lastSelectedDataset = dataID;
        }
        
        if(scaleFactor !== this.lastSelectedScaleFactor)
        {
            scaleFactorChanged = true;
            this.lastSelectedScaleFactor = scaleFactor;
        }

        if(compMethod !== this.lastSelectedCompMethod)
        {
            compMethodChanged = true;
            this.lastSelectedCompMethod = compMethod;
        }

        console.log("dataset changed:" +     datasetChanged + "\n" +
                    "scaleFactor changed:" + scaleFactorChanged + "\n" + 
                    "compMethod changed:" + compMethodChanged + "\n") ;
           
        if(datasetChanged) // make changes needed for new dataset
        {
            console.log("change dataset:" + dataID);    
            this.resetBlockForDataset(dataID);
            let isValidDataset = this.isValidDataset(dataID)
            this.updateDatasetBbox(dataID, isValidDataset);
            this.updateDatasetUrl(dataID, isValidDataset);
            if(isValidDataset)
            {
                this.resetScale(dataID, scaleFactor);
                this.resetDates(dataID);
            }
            return ;
        }

        if(scaleFactorChanged && this.isValidDataset(dataID)) 
        {
            // update scale to reflect new scale factor
            console.log("change scaleFactor:" + scaleFactor);    
            this.resetScale(dataID, scaleFactor);
            return;
        }

        

    },
      /**
    * convenience method that offsets an input date by specified 
    * number of years, months and days
    * @param {!string} date ISO Format date string e.g. 2009-01-01T00:00:00Z
    * @param {number} [years] Number of years to deduct from date
    * @param {number} [months] Number of months to deduct from date
    * @param {number} [days] Number of days (days in month) to deduct from date
    * @returns {string} Shortend date string e.g. 2007-01-01
    * */
    getDateOffset(date, yrs, months,days){
        var d2 = new Date(date);
        console.log("d2:" +d2) ;
        let c2 = new Date(d2.getFullYear() - yrs, d2.getMonth() - months, d2.getDate() - days );
        c2 = c2.toISOString();
        c2 = this.getISODateShortFormat(c2);
        return c2 ;
    },
    /**
    * convenience method that shortens ISO format data string to short date string 
    * used by Blockly DataField
    * @param {!string} isodate ISO Format date string e.g. 2009-01-01T00:00:00Z
    * @returns {string} Shortend date string e.g. 2007-01-01
    * */

    getISODateShortFormat(isodate){
        let c2 = isodate.toString().split('T')[0];
        return c2 ;
    },
    /**
    * Reset To and From date fields in Block with values derived from STAC metadata 
    * for the specified dataset
    * @param {!string} dataID the ID of the dataset as listed in STAC catalog  
    * */

    resetDates(dataID)
    {
        let metadata = this.getDatasetMetaData(dataID);
        if(metadata.extent)
        {
            let temporalExtent = metadata.extent.temporal.interval ;
            if(! temporalExtent[0] )
            {
                console.error("no start date in temporal extent interval data") ;
                return ; // should always have temproal extent in metadata and from date
            }
            let extentFrom = temporalExtent[0] ;
            let extentTo = temporalExtent[1] ;
            let extentToISO = "" ;
            let extentFromISO = "" ;

            if(extentTo)
            {
                extentToISO = this.getISODateShortFormat(extentTo) ;
                this.setFieldValue(extentToISO,'td');
            }
            else
            {
                // if metadata does not specify an end date used todays date minus 
                // five days to allow some time for earthengine updates 
                // TODO - use cadence field if avaialble to work out offset
                extentToISO = this.getDateOffset(new Date().toISOString(), 0,0,5)
                // set to To Date 
                this.setFieldValue(extentToISO ,'td');
            }
            
            extentFromISO = this.getISODateShortFormat(extentFrom) ;
            // set the From Date
            this.setFieldValue(extentFromISO,'fd');
           
        }
    },
 
 

    /**
     * checks if the specfied ID is a valid dataset ID
     * 
    * @param {!string} dataID id of the dataset 
    * @returns {boolean} true if the dataset is list in catalog and has required properties
    * */
    
    isValidDataset(dataID)
    {
        let metadata = this.getDatasetMetaData(dataID);
        if( dataID &&
            dataID !=="Select EE ID" && 
            dataID !==""  &&
            metadata &&
            metadata.gee_type && 
            (metadata.gee_type == GEE_TYPE_IMAGE || metadata.gee_type == GEE_TYPE_IMAGE_COLLECTION)  &&
            metadata.bands &&
            metadata.bands.length > 0
        ) { return true ;} 

        return false ;
    },
    /**
     * resets the Block to show fields needed for the specified data set
     * 
    * @param {!string} dataID id of the dataset 
    * */
    
    resetBlockForDataset(dataID)
    {
        if(! this.isValidDataset(dataID)) // set block shape to defaults if invalid dataset
        {   
            console.log("invalid data id");
            this.getInput("temporal_extent").setVisible(false) ;
            this.getInput("spatial_extent").setVisible(false) ;
            this.getInput("composite_method").setVisible(false) ;        
            return;
        }
        let metadata = this.getDatasetMetaData(dataID);
        if(metadata.gee_type == GEE_TYPE_IMAGE_COLLECTION )
        {
            this.getInput("spatial_extent").setVisible(true) ;
            this.getInput("temporal_extent").setVisible(true) ;
            this.getInput("composite_method").setVisible(true) ; 
   
        }

        if(metadata.gee_type == GEE_TYPE_IMAGE )
        {
            this.getInput("temporal_extent").setVisible(false) ;
            this.getInput("spatial_extent").setVisible(true) ;
            this.getInput("composite_method").setVisible(false) ; 
        }

        

    },
    /**
     * resets the Block to show fields needed for the specified data set
     * 
    * @param {!string} dataID id of the dataset 
    * */
    
    resetScale(dataID, scaleFactor)
    {

        let metadata = this.getDatasetMetaData(dataID);
        if (!metadata.gsd) {
            this.setFieldValue('manual', "scalefactor" );
        }

        if( scaleFactor == "default") // set to native resolution of dataset
        {           
            if (metadata.gsd[0] < 1) {
                this.getField("scaleNumber").setPrecision(null);
                this.setFieldValue(metadata.gsd[0], "scaleNumber" );
            } else {
                this.getField("scaleNumber").setPrecision(1);
                this.setFieldValue(metadata.gsd[0], "scaleNumber" );
            }
        }
        
        if (scaleFactor != "default" && scaleFactor != "manual") // dont reset of manual entry
        {
            switch (scaleFactor){
                case 'scale125percent':
                    this.setFieldValue(this.getScale(metadata.gsd[0], 1.25), "scaleNumber" );
                    break;
                case 'scale150percent':
                    this.setFieldValue(this.getScale(metadata.gsd[0], 1.5), "scaleNumber" );
                    break;
                case 'scale200percent':
                    this.setFieldValue(this.getScale(metadata.gsd[0], 2.0), "scaleNumber" );
                    break;
                default:
                    console.log("scaleFactor not recognised")
                    this.setFieldValue(metadata.gsd[0], "scaleNumber" );
            }
        }
        
    },
    /**
     * Takes a gsd value and if > 1 returns a rounded number for the specified scale, otherwise returns a decimal
     */
    getScale: function(gsdVal, mod) {
        let scale = 1.0;
        if (gsdVal > 1) {
            scale = Math.round(gsdVal * mod)
        } else if (gsdVal < 1) {
            scale = gsdVal * mod;
        }
        return scale;
    },
    /**
     * Takes a dataset id and returns metadata from the dataset service
     * @param {*} id 
     * @returns 
     */
    getDatasetMetaData: function(id)
    {
        let metadata = DatasetService.getMetadata(id)
        console.debug("Returned metadata");
        console.debug(metadata);
        return metadata;
    },

    /**
     * Takes a dataset id and if the dataset is valid then tells the area service to create bbox on the map
     * if dataset is not valid then it removes any bboxes that are associated with that block
     * @param {*} id 
     * @param {*} isValidDataset 
     */
    updateDatasetBbox: function(id, isValidDataset){
        let bbox = null;
        if(isValidDataset){
            let metadata = DatasetService.getMetadata(id);
            if(metadata.extent && metadata.extent.spatial) {
                bbox = metadata.extent.spatial.bbox;
            }
        }
        
        // calling map to update bbox (null will remove from map), with block id
        AreaService.publishUpdatedDatasetBbox(bbox, this.id);
    },

    /**
     * Takes a dataset id and if the dataset has valid metadata sets a map url and whether or not a button
     * is visible
     * @param {*} id 
     * @param {*} isValidDataset 
     */
    updateDatasetUrl: function(id, isValidDataset) {
        if(isValidDataset){
            let metadata = DatasetService.getMetadata(id);
            let url = metadata.asset_url;
            if(url){
                this.getField("datasetUrlIcon").setVisible(true);
                this.datasetUrl = url;
            } else {
                this.getField("datasetUrlIcon").setVisible(false);
                this.datasetUrl = null;
            }
        } else {
            this.getField("datasetUrlIcon").setVisible(false);
            this.datasetUrl = null;
        }
    },
    /**
     * when the user clicks the help button this function gets the EE catalog url and opens
     * the url in a new tab
     */
    browseDatasets: function() {
        ManageDialogsService.infoDatasetIdDialog();
    },
    /**
     * when the user chooses a dataset without an explicit commercial license the dialog shows
     */
    licenseNotCommercial: function() {
        ManageDialogsService.commercialLicenseDialog();
    },
    /**
     * when the user clicks the map catalog button this function gets the url and opens
     * the url in a new tab
     */
    openUrl: function() {
        let block = this.sourceBlock_; // must get source block as this is in Field namespace
        let url = block.datasetUrl;
        if(url){
            // workaround to ensure it opens in new tab not new window
            window.open(url, '_blank').focus();
        }
    }
};